// SU map-structures roster loader — v0M7.0 (2026-07-25).
//
// Loads the MapStructures table from src/data/generated/dials.json
// (produced by scripts/convert-xlsx.ts against docs/Balance.xlsx
// MapStructures sheet). At v0M7.0 the sheet ships with two rows:
// `Watch Tower` and `Fort` per MAP.md §9 world-buildables lock.
//
// Road and Bridge do NOT get MapStructure rows — they carry no per-row
// state (permanent, unowned, single-dial effect). Their movement
// multipliers live as dials on the Abilities sheet:
//   - world_road_move_multiplier   (default 0.5)
//   - world_bridge_move_multiplier (default 1.0)
//
// Consumed by:
//   - v0M7.1  Sim state (WorldStructure minted from these specs)
//   - v0M7.2  apply-commands.ts (set_army_build_order validation + build_time)
//   - v0M7.3  build/capture systems (capture_days, allow_destroy_option)
//   - v0M7.4a visibility (vision_range)
//   - v0M7.4d fort effects (zoc_radius, supply_per_day, garrison_max, has_walls)
//   - v0M7.5  client build-order UI (learning_prereq gate + description tooltip)

import { assertSchemaVersion, BALANCE_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/dials.json';

export interface MapStructureSpec {
  /** Free-text description — flavour + UI tooltip. */
  readonly description: string;
  /**
   * Days the Engineer-Army must remain parked on the target cell to
   * complete construction. Sim ticks are converted via
   * `buildTimeDays × TICKS_PER_GAME_DAY × SECONDS_PER_TICK` to reach the
   * per-tick completion threshold (matches v0M5.4d recruit-queue math).
   */
  readonly buildTimeDays: number;
  /**
   * Knowledge/tech prereq (matches KnowledgeTree.xlsx `Knowledge Name`).
   * Empty string = no prereq. Enforced by apply-commands.ts
   * set_army_build_order against faction.researchedKnowledge.
   */
  readonly learningPrereq: string;
  /**
   * Days an enemy Army must stay parked on the cell before ownership
   * flips (or the structure is destroyed if the attacker picks the
   * destroy option). 0 = not park-capturable — Fort uses siege battle
   * instead per MAP.md §9.2.
   */
  readonly captureDays: number;
  /**
   * If true, attacker may pick 'destroy' at capture-order issue rather
   * than 'capture'. Watch Tower at v1 — false for Fort (siege battle
   * outcome is capture-only).
   */
  readonly allowDestroyOption: boolean;
  /**
   * Cells of vision this structure contributes to its owning faction's
   * visibility map (v0M5.1 BFS system). 0 = no vision contribution.
   * Watch Tower at v1 = 3.
   */
  readonly visionRange: number;
  /**
   * ZoC radius in cells. 0 = no ZoC. Fort at v1 = 4 (comparable to
   * settlement ZoC).
   */
  readonly zocRadius: number;
  /**
   * Supplies granted per day to friendly Armies inside the structure's
   * ZoC. 0 = no supply provision. Fort at v1 = 50 (mirrors settlement-
   * in-ZoC supply pattern per MECHANICS.md §5).
   */
  readonly supplyPerDay: number;
  /**
   * Max garrison cohorts the structure can hold. 0 = not garrisonable.
   * Fort at v1 = 3.
   */
  readonly garrisonMax: number;
  /**
   * True iff assault triggers a wall-battlemap template. Fort at v1
   * (reuses settlement-walls template at reduced scale). Consumed by
   * battle-factory when routing fort assault battles.
   */
  readonly hasWalls: boolean;
}

export type MapStructures = Readonly<Record<string, MapStructureSpec>>;

/** Load the map-structures roster. Fails loudly on schemaVersion mismatch.
 *  Returns an empty object if the MapStructures sheet was absent from the
 *  xlsx at build time (retro-compat pattern matching SpecialUnits). */
export function loadMapStructures(): MapStructures {
  assertSchemaVersion(rawBlob, BALANCE_SCHEMA_VERSION, 'src/data/generated/dials.json');
  return (rawBlob as unknown as { mapStructures?: MapStructures }).mapStructures ?? {};
}

/** Retrieve a map structure by name; throw on unknown names. */
export function getMapStructure(structures: MapStructures, name: string): MapStructureSpec {
  const entry = structures[name];
  if (!entry) {
    throw new Error(
      `Unknown map structure: ${name}. Known: ${Object.keys(structures).join(', ') || '(none)'}`,
    );
  }
  return entry;
}
